/* AEVART local-calendar recurrence. Fixed anchors; no visitor/session reset. */
(function (root) {
  'use strict';
  const DAY = 86400000, cache = new Map();
  const fields = ['startDate','startTime','endDate','endTime','unit','interval','count','weekday','monthday'];
  function utc(p) { return Date.UTC(p.y, p.m - 1, p.d, p.h || 0, p.min || 0, p.s || 0); }
  function date(value, name) {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value || ''));
    if (!m) throw Error(name + ' must be YYYY-MM-DD.');
    const p = {y:+m[1],m:+m[2],d:+m[3]}, d = new Date(utc(p));
    if (p.y < 1000 || d.getUTCFullYear() !== p.y || d.getUTCMonth()+1 !== p.m || d.getUTCDate() !== p.d) throw Error(name + ' is not a valid date.');
    return p;
  }
  function time(value, fallback, name) {
    const m = /^(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(String(value || fallback));
    if (!m || +m[1]>23 || +m[2]>59 || +(m[3]||0)>59) throw Error(name + ' must be HH:mm:ss.');
    return {h:+m[1],min:+m[2],s:+(m[3]||0)};
  }
  function integer(value, fallback, min, max, name) {
    const n = value === undefined || value === null || value === '' ? fallback : Number(value);
    if (!Number.isSafeInteger(n) || n<min || n>max) throw Error(name + ' is out of range.');
    return n;
  }
  function addDays(p, n) {
    const d = new Date(Date.UTC(p.y,p.m-1,p.d+n));
    return {...p,y:d.getUTCFullYear(),m:d.getUTCMonth()+1,d:d.getUTCDate()};
  }
  function monthDate(p, n, day) {
    const d = new Date(Date.UTC(p.y,p.m-1+n,1));
    return {...p,y:d.getUTCFullYear(),m:d.getUTCMonth()+1,d:Math.min(day,new Date(Date.UTC(d.getUTCFullYear(),d.getUTCMonth()+1,0)).getUTCDate())};
  }
  function build(c, zone) {
    const fmt = new Intl.DateTimeFormat('en-GB-u-ca-gregory-nu-latn', {timeZone:zone,year:'numeric',month:'2-digit',day:'2-digit',hour:'2-digit',minute:'2-digit',second:'2-digit',hourCycle:'h23'});
    function parts(ms) {
      const p = {};
      fmt.formatToParts(ms).forEach(x=>{if(x.type !== 'literal')p[x.type]=+x.value;});
      return {y:p.year,m:p.month,d:p.day,h:p.hour,min:p.minute,s:p.second};
    }
    function instant(p) {
      const wall = utc(p), offsets = new Set();
      // Sample both sides of a DST boundary. For a repeated wall time use the
      // earlier instant; for a missing wall time advance by the actual gap.
      [-2,-1,0,1,2].forEach(n=>{const t=wall+n*DAY;offsets.add(utc(parts(t))-t);});
      const candidates=[...offsets].map(o=>wall-o).sort((a,b)=>a-b);
      const exact=candidates.filter(t=>utc(parts(t))===wall);
      if(exact.length)return exact[0];
      const later=candidates.map(t=>({t,w:utc(parts(t))})).filter(x=>x.w>wall).sort((a,b)=>a.w-b.w||a.t-b.t);
      if(!later.length)throw Error('Unable to resolve local schedule time.');
      return later[0].t;
    }
    const unit=c.unit || 'weeks';
    if(!['hours','days','weeks','months'].includes(unit))throw Error('Invalid cycle unit.');
    const interval=integer(c.interval,1,1,10000,'Cycle interval');
    const count=integer(c.count,0,0,100000000,'Cycle count');
    const weekday=integer(c.weekday,1,0,6,'Weekday');
    const monthday=integer(c.monthday,1,1,31,'Month day');
    let anchor={...date(c.startDate||'2026-10-26','Start date'),...time(c.startTime,'00:00:00','Start time')};
    const requestedStart=instant(anchor);
    if(unit==='weeks')anchor=addDays(anchor,(weekday-new Date(utc(anchor)).getUTCDay()+7)%7);
    if(unit==='months'){
      const candidate=monthDate(anchor,0,monthday);
      anchor=instant(candidate)<requestedStart?monthDate(anchor,1,monthday):candidate;
    }
    const first=instant(anchor);
    let stop=Infinity;
    if(c.endDate){
      // Global end is exclusive, matching the editor help text.
      stop=instant({...date(c.endDate,'End date'),...time(c.endTime,'00:00:00','End time')});
      if(stop<=requestedStart)throw Error('End must be after start.');
    }
    const boundaries=new Map([[0,first]]);
    function boundary(i){
      if(boundaries.has(i))return boundaries.get(i);
      let value;
      if(unit==='hours')value=first+i*interval*3600000;
      else if(unit==='months')value=instant(monthDate(anchor,i*interval,monthday));
      else value=instant(addDays(anchor,i*interval*(unit==='weeks'?7:1)));
      if(!Number.isFinite(value))throw Error('Schedule is outside the supported date range.');
      if(boundaries.size>8)boundaries.delete(boundaries.keys().next().value);
      boundaries.set(i,value);return value;
    }
    return function(now){
      if(now<first)return {active:false,start:first,end:null,index:-1};
      if(now>=stop)return {active:false,start:null,end:null,index:-1};
      const local=parts(now);
      let i;
      if(unit==='hours')i=Math.floor((now-first)/(interval*3600000));
      else if(unit==='months')i=Math.floor(((local.y-anchor.y)*12+local.m-anchor.m)/interval);
      else i=Math.floor((utc({...local,h:0,min:0,s:0})-utc({...anchor,h:0,min:0,s:0})) / (DAY*interval*(unit==='weeks'?7:1)));
      i=Math.max(0,i);
      while(i>0&&now<boundary(i))i--;
      while(now>=boundary(i+1))i++;
      if(count>0&&i>=count)return {active:false,start:null,end:null,index:i};
      const start=boundary(i),end=Math.min(boundary(i+1),stop);
      return {active:now>=start&&now<end,start,end,index:i};
    };
  }
  function getWindow(config, nowMs, timezone) {
    try {
      if(!Number.isFinite(nowMs))throw Error('Invalid current time.');
      const c=config||{},zone=timezone||'UTC';
      const key=JSON.stringify([zone,...fields.map(k=>c[k])]);
      if(!cache.has(key)){
        if(cache.size>=8)cache.delete(cache.keys().next().value);
        cache.set(key,build(c,zone));
      }
      return cache.get(key)(nowMs);
    } catch(e) {return {active:false,start:null,end:null,index:-1,error:e.message};}
  }
  const api={getWindow};
  if(typeof module!=='undefined'&&module.exports)module.exports=api;
  if(root)root.AevSaleSchedule=api;
})(typeof window!=='undefined'?window:typeof globalThis!=='undefined'?globalThis:this);

/* AEVART campaign banner: one observer and passive, frame-batched scroll work. */
(()=>{
 if(customElements.get('aev-sale-banner'))return;
 let introShown=false;
 class SaleBanner extends HTMLElement{
  connectedCallback(){
   this.mode=null;this.frame=0;this.abort=new AbortController();this.root=document.documentElement;
   this.config=JSON.parse(this.querySelector('[data-sale-config]').textContent);
   this.bar=this.querySelector('.aev-sale__bar');this.reserve=this.querySelector('.aev-sale__reserve');
   this.intro=this.querySelector('.aev-sale__intro');this.social=this.querySelector('.aev-sale__social');
   this.root.classList.add('aev-sale-active');this.classList.add('is-enhanced');
   if(this.intro){this.intro.classList.remove('is-collapsing');this.intro.inert=false;this.intro.hidden=true;}
   if(this.config.markerEnabled&&this.config.designMode){
    this.marker=document.createElement('div');this.marker.className='aev-sale-marker';
    this.marker.style.top=this.config.markerY+'px';
    this.marker.innerHTML='<span>AEVART banner visibility marker · '+this.config.markerY+'px</span>';document.body.append(this.marker);
   }
   const opts={signal:this.abort.signal};
   this.expandButton=this.querySelector('[data-sale-expand]');
   this.expandButton?.addEventListener('click',()=>this.expand('manual'),opts);
   this.querySelectorAll('[data-sale-collapse]').forEach(b=>b.addEventListener('click',()=>this.collapse(),opts));
   document.addEventListener('keydown',e=>{if(e.key==='Escape')this.collapse();},opts);
   document.addEventListener('click',e=>{if(e.target.closest('.header-section, [data-js-toggle]'))this.collapse();},opts);
   window.addEventListener('scroll',()=>this.queueLayout(),{...opts,passive:true});
   window.addEventListener('resize',()=>this.queueLayout(),{...opts,passive:true});
   document.addEventListener('visibilitychange',()=>{this.tick();this.startClock();},opts);
   this.observer=new ResizeObserver(()=>this.queueLayout());this.observer.observe(this.bar);
   this.tick();this.layout();this.startClock();
   if(this.intro&&this.config.home&&this.config.introSeconds>0&&!introShown&&scrollY<30){
    introShown=true;this.expand('auto');
   }
  }
  expand(mode){
   if(!this.intro)return;
   clearTimeout(this.introTimeout);clearTimeout(this.closeTimeout);this.mode=mode;
   this.intro.classList.remove('is-collapsing');this.intro.inert=false;this.intro.hidden=false;
   this.expandButton?.setAttribute('aria-expanded','true');
   if(mode==='auto')this.introTimeout=setTimeout(()=>{if(this.mode==='auto')this.collapse();},this.config.introSeconds*1000);
   else this.intro.querySelector('[data-sale-collapse]')?.focus({preventScroll:true});
  }
  startClock(){clearInterval(this.clock);if(!document.hidden&&this.config.timerEnabled)this.clock=setInterval(()=>this.tick(),1000);}
  tick(){
   let result={active:false};
   if(this.config.timerEnabled){
    const zone=this.config.timezoneMode==='fixed'?this.config.timezone:Intl.DateTimeFormat().resolvedOptions().timeZone;
    result=window.AevSaleSchedule.getWindow(this.config,Date.now(),zone);
   }
   this.querySelectorAll('[data-sale-timer]').forEach(el=>{el.hidden=!result.active;});
   if(result.active){
    const seconds=Math.max(0,Math.ceil((result.end-Date.now())/1000));
    const days=seconds>86400?Math.floor(seconds/86400):0;
    const value=(days?days+'D ': '')+String(Math.floor(seconds/3600)-(days*24)).padStart(2,'0')+'H : '+String(Math.floor(seconds/60)%60).padStart(2,'0')+'M : '+String(seconds%60).padStart(2,'0')+'S';
    this.querySelectorAll('[data-sale-time]').forEach(el=>{if(el.textContent!==value)el.textContent=value;});
   }
   const error=this.querySelector('[data-sale-error]');
   if(error){error.hidden=!result.error;error.textContent=result.error?'Timer settings: '+result.error:'';}
  }
  queueLayout(){if(!this.frame)this.frame=requestAnimationFrame(()=>{this.frame=0;this.layout();});}
  layout(){
   const height=this.bar.getBoundingClientRect().height;
   this.reserve.style.height=height+'px';
   const away=this.config.markerEnabled&&scrollY>0&&scrollY+innerHeight>=this.config.markerY;
   this.classList.toggle('is-away',away);this.bar.inert=away;
   this.root.style.setProperty('--aev-sale-offset',away?'0px':height+'px');
   if(this.social){
    const text=this.bar.querySelector('.aev-sale__copy').getBoundingClientRect();
    const socialWidth=this.social.scrollWidth;
    this.classList.toggle('has-social-room',text.left>socialWidth+48);
   }
   if(away&&this.mode!=='manual')this.collapse();
  }
  collapse(){
   if(!this.intro||this.intro.hidden||this.intro.classList.contains('is-collapsing'))return;
   const restoreFocus=this.intro.contains(document.activeElement);
   clearTimeout(this.introTimeout);this.mode=null;this.expandButton?.setAttribute('aria-expanded','false');this.intro.classList.add('is-collapsing');
   if(restoreFocus){const target=this.bar.inert?document.querySelector('.header-section button, .header-section a'):(this.expandButton||this.bar);if(target){if(target===this.bar)target.tabIndex=-1;target.focus({preventScroll:true});}}
   this.intro.inert=true;
   this.closeTimeout=setTimeout(()=>{if(this.intro)this.intro.hidden=true;},matchMedia('(prefers-reduced-motion:reduce)').matches?0:540);
  }
  disconnectedCallback(){
   this.abort?.abort();this.observer?.disconnect();clearInterval(this.clock);clearTimeout(this.introTimeout);clearTimeout(this.closeTimeout);cancelAnimationFrame(this.frame);this.frame=0;this.classList.remove('is-enhanced');
   this.marker?.remove();this.root?.classList.remove('aev-sale-active');this.root?.style.removeProperty('--aev-sale-offset');
  }
 }
 customElements.define('aev-sale-banner',SaleBanner);
})();
